"use client"

import styled, { css } from "styled-components"
import { IntentPrefetchLink } from "@/components/Link"

const styles = css`
  ${getGeneralStyles}
  ${getFontStyles}
  ${getBorderRadiusStyles}
  ${getPaddingStyles}
  ${getColorStyles}
  ${getHoverActiveStyles}
  ${getDisabledStyles}
`

function getGeneralStyles({ $width }) {
  return css`
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
    width: ${$width};
  `
}

function getFontStyles({ theme, $small, $jumbo }) {
  const { size, lineHeight, weight, family } = theme.fonts
  const { extraSmall, xxs, small } = theme.spaces
  if ($small) {
    return css`
      font-family: var(${family.primary});
      font-size: ${size.xtraSmallParagraph};
      line-height: ${lineHeight.oneAndHalf};
      font-weight: ${weight.bold};
      gap: ${extraSmall};
    `
  }
  if ($jumbo) {
    return css`
      font-family: var(${family.primary});
      font-size: ${size.paragraph};
      line-height: ${lineHeight.singleNThird};
      font-weight: ${weight.bold};
      gap: ${small};
    `
  }
  return css`
    font-family: var(${family.primary});
    font-size: ${size.smallParagraph};
    line-height: ${lineHeight.single};
    font-weight: ${weight.bold};
    gap: ${xxs};
  `
}

function getBorderRadiusStyles({ theme, $rounded, $jumbo }) {
  const { extraSmall, standard, rounded } = theme.borders.radius
  if ($rounded) {
    return css`
      border-radius: ${rounded}; // 100px
    `
  }
  if ($jumbo) {
    return css`
      border-radius: ${extraSmall}; // 4px
    `
  }
  return css`
    border-radius: ${standard}; // 6px
  `
}

function getPaddingStyles({ theme, $rounded, $small, $jumbo }) {
  const { xxs, extraSmall, small, large } = theme.spaces
  if ($small && $rounded) {
    return css`
      padding: ${xxs} ${small}; // 6px 12px
    `
  }
  if ($jumbo && $rounded) {
    return css`
      padding: ${small} ${large}; // 12px 24px
    `
  }
  if ($rounded) {
    return css`
      padding: ${extraSmall} ${small}; // 4px 12px
    `
  }
  if ($jumbo) {
    return css`
      padding: ${small}; // 12px
    `
  }
  return css`
    /* border: none; */
    padding: ${extraSmall};
  `
}

function getColorStyles({ theme, $variant, $disabled }) {
  if ($disabled) return
  const { colors } = theme
  const { bg } = theme.widgets.button.colors.standard

  if ($variant !== "standard") {
    const { fg, bg } = colors[$variant]
    return css`
      color: ${fg};
      background-color: ${bg};
    `
  }
  return css`
    color: ${colors.fg};
    background-color: ${bg};
  `
}

function getHoverActiveStyles({ theme, $disabled, $variant }) {
  if ($disabled) return
  const { active } = theme.widgets.button.colors[$variant]
  const { fg } = theme.colors[$variant]
  return css`
    &:hover,
    &:active,
    &:target {
      background-color: ${active};
    }
    &:visited {
      color: ${fg};
    }
  `
}

function getDisabledStyles({ theme, $disabled, $variant }) {
  if (!$disabled) return
  const { disabled, standard } = theme.widgets.button.colors
  const { fg, bg } = theme.colors.disabled

  let style = css`
    cursor: not-allowed;
    color: ${fg};
    background-color: ${bg};
  `

  if ($variant === "secondary") {
    style = css`
      ${style}
      background-color: ${disabled.secondary};
    `
  }

  if ($variant === "standard") {
    style = css`
      ${style}
      background-color: ${standard.bg};
    `
  }
  return style
}

export const StyledButton = styled.button`
  ${styles}
`

export const StyledLink = styled(IntentPrefetchLink)`
  ${styles}
`
